import {GridColDef} from '@mui/x-data-grid-pro';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {themeColors} from '../../../theme/Theme';
import {serverToBuilderTypeLabel} from '../constants/SolutionType';
import {URGENCY} from '../../../constants/Urgency';
import {RenderColor} from '../../../components/EditorGrid/GridRenderComponents';
import {
  ADVICE_ACCEPTED_STATUS,
  builderGridRowActions,
  DRAFTING_ADVICE_STATUS,
} from './builderColumns';
import {renderCellDateTime} from '../../../utils/renderCellDateTime';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {newUTCDate} from '../../../utils/newUTCDate';
import {dateDifferenceInDays} from '../../../utils/dateUtils';
import {OVBuilders_ovrecommends} from '../../../schema-types/OVBuilders';

export const AWAITING_SUBMISSION_STATUS = 'Awaiting submission';

const allSPPValues = [
  'Error',
  'Draft',
  'Submitted',
  AWAITING_SUBMISSION_STATUS,
  'In progress',
  'Complete',
  'Complete with cancellation',
  'Cancelled',
];

const isOver30days = (today: Date, dateCreated?: string) => {
  if (!dateCreated) {
    return false;
  }

  return (
    dateDifferenceInDays({
      date: newUTCDate(dateCreated),
      today,
    }) > 30
  );
};

export const sppDAValueGetterStatus = (value: never, row: OVRecommendation) => {
  if (row.recommends?.some((rec) => rec.isFailedSubmission)) {
    return 'Error';
  }
  if (!row.isFinalised) {
    return 'Draft';
  }

  if (
    row.progressTrackerStatus === 'Submitted' &&
    isOver30days(new Date(), row.dateCreated)
  ) {
    return 'Complete';
  }

  if (row.recommends?.[0]?.recommendationStatus === DRAFTING_ADVICE_STATUS) {
    return AWAITING_SUBMISSION_STATUS;
  }

  if (row.recommends?.[0]?.recommendationStatus === ADVICE_ACCEPTED_STATUS) {
    return isOver30days(new Date(), row.dateCreated) ? 'Complete' : 'Submitted';
  }

  return row.progressTrackerStatus;
};

export const valueGetterAdvice = (value: never, row: OVRecommendation) => {
  const label = serverToBuilderTypeLabel(row.solutionType);
  return label;
};

export const valueGetterName = (value: never, row: OVRecommendation) => {
  return row.name || 'No name given';
};

export const valueGetterGoal = (value: never, row: OVRecommendation) => {
  return row.goal?.name;
};

export const valueGetterUrgency = (value: never, row: OVRecommendation) => {
  return row.urgency;
};

const NEW_PLAN_ADVICE = 'Setup a new ';
export const valueGetterCategory = (value: never, row: OVRecommendation) => {
  if (!row.action) {
    return 'Action';
  }
  switch (row.action) {
    case OVRecommendActionEnum.ACTION:
      return 'Action';
    case OVRecommendActionEnum.WITHDRAW:
      return 'Withdraw';
    case OVRecommendActionEnum.SET_UP_NEW:
      return 'Set up New';
    case OVRecommendActionEnum.CONTRIBUTE:
      if (row.recommends?.some((rec) => rec.advice?.includes(NEW_PLAN_ADVICE))) {
        return 'Set up New | Contribute';
      }
      return 'Contribute';
    case OVRecommendActionEnum.MODIFY:
      return 'Modify';
    case OVRecommendActionEnum.TRANSFER:
      if (row.recommends?.some((rec) => rec.advice?.includes(NEW_PLAN_ADVICE))) {
        return 'Set up New | Transfer';
      }
      return 'Transfer';
    default:
      return row.action;
  }
};

export const sppDAColumns: (GridColDef & {
  field: keyof OVBuilders_ovrecommends;
})[] = [
  {
    field: 'id',
    headerName: 'Status',
    flex: 0.7,
    headerAlign: 'center',
    valueGetter: sppDAValueGetterStatus,
    renderCell: RenderColor({
      Submitted: themeColors.lightBlue,
      Draft: themeColors.greyLight,
      Failed: themeColors.red,
      Error: themeColors.red,
      [AWAITING_SUBMISSION_STATUS]: themeColors.lightGreen,
      'In progress': themeColors.blue,
      Complete: themeColors.green,
      'Complete with cancellation': themeColors.coral,
      Cancelled: themeColors.lightPurple,
    }),
  },
  {
    field: 'name',
    headerName: 'Description',
    flex: 2,
    headerAlign: 'center',
    valueGetter: valueGetterName,
  },
  {
    field: 'action',
    headerName: 'Category',
    flex: 1,
    headerAlign: 'center',
    valueGetter: valueGetterCategory,
    renderCell: RenderColor(
      {
        Withdraw: themeColors.lightGreen,
        Contribute: themeColors.chartPurple,
        Modify: themeColors.coral,
        Transfer: themeColors.lightBlue,
        'Set up New': themeColors.amber,
        'Set up New | Contribute': themeColors.lightPink,
        'Set up New | Transfer': themeColors.lightPurple,
        'Charge an ad-hoc fee': themeColors.lightYellow,
        Action: themeColors.lightAlert,
        default: themeColors.greyLight,
      },
      true
    ),
  },
  {
    field: 'urgency',
    headerName: 'Urgency',
    flex: 1,
    headerAlign: 'center',
    valueGetter: valueGetterUrgency,
    renderCell: RenderColor({
      [URGENCY.NORMAL_ID]: themeColors.greyLight,
      [URGENCY.TAX_ID]: themeColors.lightGreen,
      [URGENCY.URGENT_ID]: themeColors.lightAlert,
      default: themeColors.greyLight,
    }),
  },
  // {
  //   field: 'dateCreated',
  //   headerName: 'Date Created',
  //   flex: 1,
  //   headerAlign: 'center',
  //   renderCell: renderCellDateTime,
  //   valueGetter: (value, row, column, apiRef) => row.dateCreated?.valueOf(), // used for ordering
  // },
  {
    field: 'review',
    headerName: 'Date Submitted',
    flex: 1,
    headerAlign: 'center',
    valueGetter: (value, row: OVBuilders_ovrecommends, column, apiRef) => {
      if (row.review?.dateSubmitted) {
        return row.review?.dateSubmitted;
      }
      return null;
    },
    renderCell: renderCellDateTime,
  },
  {
    field: '__typename',
    headerName: ' ',
    flex: 0.6,
    headerAlign: 'center',
    renderCell: builderGridRowActions,
  },
];

export const sppBuilderColumns = sppDAColumns;
